import type { VatNumber } from '~~/helpers/cart/customFields';
import { getSelfDeclaration } from '~~/helpers/cart/customFields';
import { getVatPrefix } from '~~/helpers/formatVatNumberForLocale';
import {
  ADDRESS_VALIDATION_STATUS,
  B2B_FALLBACK_TAX_RATE,
  VAT_NUMBER_VALIDATION_STATUS,
} from '~~/constants/vatValidationStatus';
import { MILLISECONDS_IN_SECOND, SECONDS_IN_MINUTE } from '~~/constants/timeUnits';
import type { VatValidationError } from '~~/constants/vatValidationStatus';
import type { Address } from '~~/types/commercetools';
import type {
  AddressMismatches,
  AddressValidationResult,
  VatCompanyAddress,
  VatValidationResult,
  VatValidationResponse,
} from '~~/types/vatValidation';
import { STATUS_ERROR } from '~~/constants/http';
import { ADDRESS_TYPE } from '~~/types/AddressType';
import { getAddressMismatch, getJudgedAddressKey } from '~~/helpers/vatValidation/getAddressMismatch';
import { isBzstValidatorCountry } from '~~/helpers/vatValidation/isBzstValidatorCountry';

export const useVatValidation = () => {

  const i18n = useI18n();
  const {
    cart,
    isExternalTaxMode,
    loading: cartLoading,
    load: reloadCart,
    setUpdateCustomCartError,
  } = useCartExtended();
  const {
    isChangeTaxRateEnabled,
    isB2bFallbackTaxEnabled,
    isBillingAddressValidationEnabled,
  } = useStoreVsfExtended();
  const vatNumberValidationError = useState<VatValidationError>('useVatValidationError', () => '');
  const addressValidation = useState<AddressValidationResult | null>('useVatValidationAddressResult', () => null);
  const vatNumber = useState<VatNumber>('useVatValidationVatNumber', () => '');
  const companyAddress = useState<VatCompanyAddress | null>('vatCompanyAddress', () => null);
  const vatResult = useState<VatValidationResult | null>('useVatValidationVatResult', () => null);
  const { countryCode } = useLocalization();
  const { clientType } = useCheckoutClientType(ADDRESS_TYPE.Billing);
  const config = useRuntimeConfig();

  const isBzstMarket = computed(() => isBzstValidatorCountry({
    bzstCountries: config?.public?.bzstValidatorCountries,
    country: countryCode.value,
  }));

  const fullVatNumber = computed<string>(() => {

    return `${vatPrefix.value}${vatNumber.value}`;

  });

  const setVatNumber = (value: VatNumber) => {

    vatNumber.value = value;

  };

  const request = useState<Promise<void> | null>('useVatValidationPromise', () => null);

  const loading = computed(() => !!request.value);

  /**
   * Every address validation covers the whole address, so only the newest one is
   * authoritative - starting one aborts whatever is still in flight.
   */
  const addressValidationController = useState<Maybe<AbortController>>(
    'useVatValidationAddressController',
    () => null,
  );

  /**
   * Address fields edited since the last authoritative response, so the form can show a
   * loader next to each of them. They pile up while the user keeps typing and are cleared
   * as a whole, because the response that lands answers for all of them at once.
   */
  const validatingAddressFields = useState<Set<string>>('useVatValidationAddressFields', () => new Set());

  /**
   * The address the newest validation request was sent for. Asking about an unchanged address -
   * blurring a field without editing it, or re-entering a checkout that still holds the same
   * address - asks the exact same question, so it is skipped: the answer we already hold still
   * applies. Only the fields the validation actually judges count, so leaving e.g. the phone
   * number does not re-run it either.
   */
  const validatedAddressKey = useState<Maybe<string>>('useVatValidationAddressKey', () => null);

  /**
   * An autofilled address is a copy of the registry answer the VAT number validation just
   * returned, so validating it would re-ask a question we already hold the answer to. The match
   * is verified rather than assumed - if the autofill left any judged field differing from the
   * registry, the address stays unvalidated and the first blur checks it as usual.
   */
  const markAddressAsValidated = (address: Partial<Address>): void => {

    if (!companyAddress.value) {

      return;

    }

    const mismatches = getAddressMismatch(address, companyAddress.value);

    if (Object.keys(mismatches).length > 0) {

      return;

    }

    validatedAddressKey.value = getJudgedAddressKey(address);

  };

  const cancelAddressValidation = () => {

    addressValidationController.value?.abort();
    addressValidationController.value = null;
    validatingAddressFields.value.clear();
    validatedAddressKey.value = null;

  };

  const isValidationInProgress = computed(() => loading.value || !!addressValidationController.value);

  const areAmountsAwaitingValidation = computed(() =>
    isChangeTaxRateEnabled.value && isValidationInProgress.value);

  const hasMissingRegistryAddress = computed(() =>
    addressValidation.value?.status === ADDRESS_VALIDATION_STATUS.REGISTRY_ADDRESS_MISSING);

  const hasAddressMismatch = computed(() =>
    addressValidation.value?.status === ADDRESS_VALIDATION_STATUS.FIELD_MISMATCH);

  const isAddressMatching = computed(() =>
    addressValidation.value?.status === ADDRESS_VALIDATION_STATUS.MATCHING);

  const hasAddressValidationFailure = computed(() =>
    hasAddressMismatch.value || hasMissingRegistryAddress.value);

  const areVatDetailsValid = computed(() => !!vatResult.value?.valid && !loading.value);
  const isVatNumberValid = computed(() =>
    !vatNumberValidationError.value && !!vatNumber.value && vatResult.value !== null && !loading.value);

  const addressMismatches = computed<AddressMismatches>(() => addressValidation.value?.mismatches ?? {});

  const getVatValidationError = (
    vatNumberValidation: VatValidationResponse['vatNumberValidation'],
  ): VatValidationError =>
    vatNumberValidation === VAT_NUMBER_VALIDATION_STATUS.VALID ||
    vatNumberValidation === VAT_NUMBER_VALIDATION_STATUS.SKIPPED
      ? ''
      : vatNumberValidation;

  const callValidationEndpoint = async ({
    address,
    signal,
    isAddressOnlyValidation = false,
  }: {
    address?: Partial<Address>;
    signal?: AbortSignal;
    isAddressOnlyValidation?: boolean;
  } = {}): Promise<VatValidationResponse> => {

    try {

      return await $fetch<VatValidationResponse>('/vat/v3/validate', {
        method: 'POST',
        signal,
        body: {
          vatNumber: fullVatNumber.value || null,
          country: countryCode.value,
          selfDeclaration: getSelfDeclaration(clientType.value),
          splitAddress: isBillingAddressValidationEnabled.value,
          cartId: cart.value?.id ?? null,
          cartVersion: cart.value?.version ?? null,
          ...(address && { address }),
          ...(isAddressOnlyValidation && { isAddressOnlyValidation }),
        },
      });

    } catch {

      return {
        vatNumberValidation: VAT_NUMBER_VALIDATION_STATUS.COULD_NOT_VALIDATE,
        companyAddress: null,
        addressValidation: null,
        rawResponse: '',
        vatResult: null,
        httpStatus: STATUS_ERROR,
      };

    }

  };

  const applyValidationResponse = (response: VatValidationResponse): void => {

    const validationError = getVatValidationError(response.vatNumberValidation);

    vatNumberValidationError.value = validationError;
    companyAddress.value = response.companyAddress;
    addressValidation.value = response.addressValidation;
    vatResult.value = response.vatResult;

    if (response.cartUpdateFailed) {

      setUpdateCustomCartError(response.message ?? 'Cart update failed');

      return;

    }

    setUpdateCustomCartError('');

  };

  const performValidation = async (address?: Partial<Address>): Promise<void> => {

    validatedAddressKey.value = getJudgedAddressKey(address);

    const response = await callValidationEndpoint({ address });
    applyValidationResponse(response);

    await reloadCart();

  };

  const validateAddress = async (address: Partial<Address>, field?: string): Promise<void> => {

    if (!isVatNumberValid.value) {

      return;

    }

    const addressKey = getJudgedAddressKey(address);

    if (addressKey === validatedAddressKey.value) {

      return;

    }

    addressValidationController.value?.abort();

    const controller = new AbortController();

    addressValidationController.value = controller;
    // Set upfront, so that blurring another untouched field while this one is still in flight
    // does not fire the same request again. A superseding request overwrites it with its own
    // address, so the key always describes the validation that is authoritative.
    validatedAddressKey.value = addressKey;

    if (field) {

      validatingAddressFields.value.add(field);

    }

    try {

      const response = await callValidationEndpoint({
        address,
        signal: controller.signal,
        isAddressOnlyValidation: true,
      });

      // A newer edit took over while we were waiting - its response is the authoritative one.
      if (addressValidationController.value !== controller) {

        return;

      }

      addressValidation.value = response.addressValidation;
      vatResult.value = response.vatResult;

      await reloadCart();

    } finally {

      if (addressValidationController.value === controller) {

        addressValidationController.value = null;
        validatingAddressFields.value.clear();

      }

    }

  };

  const requestValidateVatNumber = async (newVatNumber: VatNumber, address?: Partial<Address>): Promise<void> => {

    // Re-validating the VAT number re-answers the address too, so drop any pending address check.
    cancelAddressValidation();

    setVatNumber(newVatNumber);
    vatNumberValidationError.value = '';
    addressValidation.value = null;
    vatResult.value = null;

    await performValidation(address);

  };

  const vatPrefix = computed(() => getVatPrefix(countryCode.value));

  /**
   * A stored result answers for the VAT number together with the address it was judged against, so
   * it only stands in for a fresh one while both are still what is being asked about. An address
   * that changed away from the form poses a question the stored answer was never given to, and
   * reusing it would leave the previous address' verdict next to the new one.
   * Callers that put up no address of their own still reuse the stored result: the registry answer
   * is all they are after, and nothing but the number decides it.
   */
  const hasStoredValidation = (newVatNumber: VatNumber, address?: Partial<Address>): boolean => {

    if (vatNumber.value !== newVatNumber || vatResult.value === null) {

      return false;

    }

    return !address || getJudgedAddressKey(address) === validatedAddressKey.value;

  };

  /**
   * Resolves to whether a validation actually ran. A stored result is reused as is, and the
   * data it produced - the registry company address above all - is already applied, so callers
   * must not treat it as a fresh answer and re-apply it over what the user has since edited.
   */
  const validateVatNumber = async ({
    vatNumber,
    forceValidate = false,
    address,
  }: {
    vatNumber: VatNumber;
    forceValidate?: boolean;
    address?: Partial<Address>;
  }): Promise<boolean> => {

    const activeRequest = request.value;

    if (activeRequest) {

      await activeRequest;

    }

    if (!forceValidate && hasStoredValidation(vatNumber, address)) {

      return false;

    }

    const currentRequest = requestValidateVatNumber(vatNumber, address);
    request.value = currentRequest;

    try {

      await currentRequest;

      return true;

    } finally {

      if (request.value === currentRequest) {

        request.value = null;

      }

    }

  };

  /**
   * Waits for any in-flight validation to complete before proceeding. Validation results are stored
   * on the cart's billing address, so writing the address while validation is pending would save
   * the address before its validation verdict is ready, leaving them out of sync.
   */
  const waitForPendingValidation = async (): Promise<void> => {

    const VALIDATION_TIMEOUT_MS = SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;

    while (request.value) {

      const timeoutPromise = new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error('VAT validation timeout after 60 seconds')), VALIDATION_TIMEOUT_MS),
      );

      try {

        await Promise.race([request.value, timeoutPromise]);

      } catch (error) {

        // If timeout fired and a new request started, continue waiting for that one
        if (error instanceof Error && error.message.includes('timeout') && !request.value) {

          break;

        }

        throw error;

      }

    }

  };

  /**
   * Throws the stored verdict away without asking for a new one, so the next visit to the checkout
   * finds nothing stored and validates whatever address it is given.
   */
  const discardValidation = (): void => {

    vatNumberValidationError.value = '';
    vatNumber.value = '';
    companyAddress.value = null;
    addressValidation.value = null;
    vatResult.value = null;
    request.value = null;

    cancelAddressValidation();

  };

  const clear = async () => {

    discardValidation();

    if (cart.value?.id) {

      const response = await callValidationEndpoint();
      applyValidationResponse(response);
      reloadCart();

    }

  };

  const isErrorCouldNotValidate = computed(() =>
    vatNumberValidationError.value === VAT_NUMBER_VALIDATION_STATUS.COULD_NOT_VALIDATE);
  const isErrorNotValid = computed(() =>
    vatNumberValidationError.value === VAT_NUMBER_VALIDATION_STATUS.NOT_VALID);

  /**
   * A validation that reached no verdict leaves the registry fields unanswerable - retyping them
   * cannot change the outcome, and address validation stays off until the customer retries or
   * changes the VAT number - so they are emptied rather than left looking confirmed. Only markets
   * that fill them from the registry own them; everywhere else - BzSt markets included - the
   * customer typed them and they are none of our business.
   */
  const shouldClearRegistryAddress = computed(() =>
    isBillingAddressValidationEnabled.value && !isBzstMarket.value && isErrorCouldNotValidate.value);

  /**
   * On BzSt markets that judge the address, the rate only drops once the address was checked along
   * with the VAT number - the validate button or leaving the address page. A check of the number
   * alone, from the VAT field, is not a failed attempt at it, so it must not report one.
   */
  const isAwaitingBzstAddressCheck = computed(() =>
    isBzstMarket.value && isBillingAddressValidationEnabled.value && validatedAddressKey.value === null);

  const isNetPriceActive = computed(() => isExternalTaxMode.value && areVatDetailsValid.value);
  const isB2bFallbackTaxActive = computed(() =>
    isExternalTaxMode.value &&
    (!!vatNumberValidationError.value || hasAddressValidationFailure.value) &&
    isB2bFallbackTaxEnabled.value);

  const b2bFallbackTaxRate = B2B_FALLBACK_TAX_RATE;

  const notValidUsageMessage = computed<string>(() =>{

    if (isB2bFallbackTaxEnabled.value) {

      return i18n.t('please-enter-your-vat-id-in-the-correct-format-or-vat-will-be-charged-at-percentage',
        { percentage: b2bFallbackTaxRate });

    }

    if (isChangeTaxRateEnabled.value) {

      return i18n.t('please-enter-your-company-details-to-receive-net-if-incorrect-gross-invoice');

    }

    return i18n.t('Please correct it or continue anyway.');

  });
  const validChangeTaxRateUsageMessage = computed<string>(() =>
    isNetPriceActive.value
      ? i18n.t('Prices have been adjusted to NET.')
      : i18n.t(
        'We were unable to adjust prices to NET. You can continue and contact our support service to claim the ' +
        'tax back.',
      ),
  );
  const changeTaxRateSuccessMessage = computed<string>(() =>
    isNetPriceActive.value
      ? ''
      : i18n.t('Technical issue occurred.'),
  );

  const messages = computed<string[]>((previousMessages) => {

    if (cartLoading.value || isValidationInProgress.value) {

      return previousMessages ?? [];

    }

    if (hasMissingRegistryAddress.value) {

      return [
        i18n.t('We could not validate the company address for this VAT ID.'),
      ];

    }

    if (
      !vatNumberValidationError.value &&
      hasAddressMismatch.value
    ) {

      return ['', notValidUsageMessage.value];

    }

    if (!vatNumberValidationError.value && !hasAddressValidationFailure.value) {

      return (isChangeTaxRateEnabled.value && isVatNumberValid.value && !isAwaitingBzstAddressCheck.value)
        ? [changeTaxRateSuccessMessage.value, validChangeTaxRateUsageMessage.value]
        : [];

    }

    return (isErrorNotValid.value)
      ? [
        i18n.t('VAT ID not validated.'),
        notValidUsageMessage.value,
      ]
      : [
        i18n.t('VAT ID validation is taking too much time.'),
        i18n.t('Confirm by clicking the button below.'),
      ];

  });

  return {
    areVatDetailsValid,
    isVatNumberValid,
    isErrorCouldNotValidate,
    isErrorNotValid,
    shouldClearRegistryAddress,
    isBzstMarket,
    vatPrefix,
    vatNumberValidationError,
    loading,
    isValidationInProgress,
    areAmountsAwaitingValidation,
    validatingAddressFields,
    messages,
    validateVatNumber,
    hasStoredValidation,
    waitForPendingValidation,
    validateAddress,
    markAddressAsValidated,
    clear,
    discardValidation,
    isNetPriceActive,
    isB2bFallbackTaxActive,
    b2bFallbackTaxRate,
    vatNumber,
    vatResult,
    companyAddress,
    addressValidation,
    addressMismatches,
    hasAddressValidationFailure,
    hasMissingRegistryAddress,
    hasAddressMismatch,
    isAddressMatching,
  };

};
